import { useEffect, useState, useRef } from 'react';
import { Box, useTheme, Button } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens, useHideMode } from "../../theme";
import SaveIcon from '@mui/icons-material/Save';
import Header from "../../components/Header";
import useAxiosPrivate from "../../hooks/useAxiosPrivate"
import saveInvoices from '../../components/managers/saveInvoices';
import { useNavigate, useLocation } from 'react-router-dom';
import handleError from "../../hooks/handleError";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import RotateRightIcon from '@mui/icons-material/RotateRight';


const Invoices = () => {
    useEffect(() => {
        document.title = 'Fretless Finance | Invoices';
    }, []);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const hideTheme = useHideMode();
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    var [invoices, setInvoices] = useState([]);
    var [displayedInvoices, setDisplayedInvoices] = useState([]);
    var [saveInvoicesError, saveInvoicesError] = useState('');
    var err = false;
    const errRef = useRef();
    const effectRan = useRef(false);
    var isMounted = false;
    const controller = new AbortController();
    const [loading, setLoading] = useState(false);



    // Get Invoices
    useEffect(() => {
        if (effectRan.current === false) {
            let isMounted = true;
            const controller = new AbortController();

            const getInvoices = async () => {
                try {
                    var getInvoicesResponse = await axiosPrivate.get('/invoices', {
                        signal: controller.signal
                    });
                    isMounted && setInvoices(getInvoicesResponse.data)
                    setDisplayedInvoices(getInvoicesResponse.data);
                } catch (err) {
                    if ((err.response?.status === 401) || (err.response?.status === 403)) {
                        navigate('/sign-in', { state: { from: location }, replace: true });
                    }
                    else {
                        handleError(err);
                    }
                }
            }

            getInvoices();
            effectRan.current = true;
        }

        return () => {
            isMounted = false;
            controller.abort();

        }

    }, [])

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            var Response = await axiosPrivate.get('/invoices/fetch', {
                signal: controller.signal
            });

            setInvoices(Response.data)
            setDisplayedInvoices(Response.data);
        } catch (err) {
            if ((err.response?.status === 401) || (err.response?.status === 403)) {
                navigate('/sign-in', { state: { from: location }, replace: true });
            }
            else {
                handleError(err);
            }
        }
        setLoading(false);
    }


    const handleUpdateClick = event => {
        fetchInvoices();
    }

    function handleRenderCell(params, formatValue = false, formatUSD = false, formatDate = false,) {
        const colName = params.colDef.field;
        const { value } = params;


        const getFormattedValue = () => {
            if (!formatValue && !formatDate) {
                return value;
            }

            if (formatDate) {
                const date = new Date(0); // create a new Date object with 0 milliseconds since the Unix epoch
                date.setUTCSeconds(value); // set the number of seconds since the Unix epoch
                return date.toLocaleDateString()

            }

            let amount = value / 100000000;
            amount = (formatUSD) ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : amount
            return amount
        };

        return (
            <div >
                {(hideTheme.mode === "hide" && (colName.includes("otal") || colName.includes("Paid") || colName.includes("mount"))) ? '*'.repeat(9) : getFormattedValue()}
            </div>
        );
    }

    const processRowUpdate = (newRow) => {
        const updatedRow = { ...newRow, actions: ((newRow.actions !== "INSERT" & newRow.actions !== "DELETE") ? "UPDATE" : newRow.actions) };
        setInvoices(invoices.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
        setDisplayedInvoices(displayedInvoices.map((row) => ((row.id === updatedRow.id & updatedRow.actions !== "DELETE") ? updatedRow : row)));
        return updatedRow;
    };

    const saveChangesHandler = event => {
        try {
            err = saveInvoices(invoices);
        } catch (err) {
            err = true;
            if (!err?.response) {
                saveInvoicesError('No Server Response');
            } else if (err.response?.status === 500) {
                saveInvoicesError('Backend Error, check your fields');
            } else if (err.response?.status === 401) {
                saveInvoicesError('Unauthorized')
            } else {
                saveInvoicesError('Error')
            }
        }

        if (!err) {
            var updatedArray = invoices.filter((row) => row.actions !== "DELETE")
            updatedArray = updatedArray.map(row => ({ ...row, actions: null }));
            setInvoices(updatedArray)
        }
        err = false;
    }

    const InvoicesColumns = [
        {
            field: "invoiceDate",
            headerName: "Invoice Date",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, false, false, true),
        },
        {
            field: "issuerName",
            headerName: "Issuer Name",
            headerAlign: 'center',
            width: 150,
            align: 'left',
            editable: false,
        },
        {
            field: "customerName",
            headerName: "Customer",
            headerAlign: 'center',
            width: 250,
            align: 'left',
            editable: true,
        },
        {
            field: "receiverName",
            headerName: "Receiver Name",
            headerAlign: 'center',
            width: 150,
            align: 'left',
            editable: false,
        },
        {
            field: "total",
            headerName: "Total",
            headerAlign: 'center',
            width: 125,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, true, true, false),
        },
        {
            field: "discountTotal",
            headerName: "Discount Total",
            headerAlign: 'center',
            width: 125,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, true, true, false),
        },
        {
            field: "dueDate",
            headerName: "Due Date",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, false, false, true),
        },
        {
            field: "status",
            headerName: "Status",
            headerAlign: 'center',
            align: 'center',
            width: 100,
            editable: false,
        },
        {
            field: "source",
            headerName: "Source",
            headerAlign: 'center',
            width: 100,
            align: 'center',
            editable: false,
        },
        {
            field: "pdfUrl",
            headerName: "PDF Link",
            headerAlign: 'center',
            width: 100,
            align: 'left',
            editable: false,
        },
        {
            field: "includedItems",
            headerName: "Included Items",
            headerAlign: 'center',
            width: 1000,
            align: 'left',
            editable: false,
        },
        {
            field: "title",
            headerName: "Title",
            headerAlign: 'center',
            width: 300,
            editable: false,
        },
        {
            field: "subhead",
            headerName: "Subhead",
            headerAlign: 'center',
            width: 300,
            align: 'center',
            editable: false,
        },
        {
            field: "amountDue",
            headerName: "Amount Due",
            headerAlign: 'center',
            width: 150,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, true, true, false),
        },

        {
            field: "amountPaid",
            headerName: "Amount Paid",
            headerAlign: 'center',
            width: 150,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, true, true, false),
        },

        {
            field: "taxTotal",
            headerName: "Tax Total",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, true, true, false),
        },
        {
            field: "currency",
            headerName: "Currency",
            headerAlign: 'center',
            width: 200,
            align: 'center',
            editable: false,
        },
        {
            field: "createdAt",
            headerName: "Created At",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, false, false, true),
        },
        {
            field: "modifiedAt",
            headerName: "Modified At",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, false, false, true),
        },
        {
            field: "lastSentAt",
            headerName: "Last Sent At",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, false, false, true),
        },
        {
            field: "lastViewedAt",
            headerName: "Last Viewed At",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            editable: false,
            renderCell: (params) => handleRenderCell(params, false, false, true),
        },
        {
            field: "requireTermsOfServiceAgreement",
            headerName: "Require ToS",
            headerAlign: 'center',
            width: 200,
            align: 'center',
            editable: false,
        },
        {
            field: "customerEmail",
            headerName: "Customer Email",
            headerAlign: 'center',
            width: 300,
            align: 'left',
            editable: true,
        },
    ];

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header
                    title="Invoices"
                    titleSize="h2"
                    subtitle="List of Invoice Balances"
                    subtitleSize="h5"
                />

                <Box>

                    {(loading) ?
                        <RotateRightIcon
                            className="rotate-icon"

                            style={{ marginRight: "0%" }}
                        />
                        :
                        ""
                    }

                    <Button
                        onClick={() => { handleUpdateClick() }}
                        sx={{
                            marginRight: "0%",
                            backgroundColor: colors.blueAccent[400],
                            color: "#ffffff",
                            '&:hover': {
                                backgroundColor: colors.blueAccent[100],
                                color: "#ffffff",
                            },
                            fontSize: "14px",
                            fontWeight: "bold",
                            padding: "10px 20px",
                        }}
                    >
                        <SyncAltIcon sx={{ mr: "10px" }} />
                        Update Invoices
                    </Button>
                </Box>
            </Box>

            < ToastContainer />

            <Box
                height="67vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "none",
                    },
                    "& .name-column--cell": {
                        color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[400],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[400],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                    },
                }}
            >

                < DataGrid

                    rows={displayedInvoices}
                    columns={InvoicesColumns}
                    processRowUpdate={processRowUpdate}
                    experimentalFeatures={{ newEditingApi: true }}
                    components={{ Toolbar: GridToolbar }
                    }
                    sx={
                        {
                            fontSize: 15,
                            '& .MuiDataGrid-cell:edit': {
                                fontSize: 10,
                            },
                            CanUserResizeColumns: true,
                        }

                    }
                />
            </Box>
            <Box padding="1rem" align="right">
                <Button onClick={saveChangesHandler}
                    sx={{
                        backgroundColor: colors.greenAccent[500],
                        color: "#ffffff",
                        '&:hover': {
                            backgroundColor: colors.greenAccent[300],
                            color: "#ffffff",
                        },
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                    }}
                >
                    <SaveIcon sx={{ mr: "10px" }} />
                    Save Changes
                </Button>
            </Box>
        </Box>

    )
};


export default Invoices;


